'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { createClient } from '@/lib/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { SupabaseClient } from '@supabase/supabase-js';
import { checkAndInstallSupernovaAgent } from '@/lib/utils/install-supernova-agent';
import { clearUserLocalStorage } from '@/lib/utils/clear-local-storage';

type AuthContextType = {
  supabase: SupabaseClient;
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [supabase, setSupabase] = useState<SupabaseClient | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Initialize Supabase client with error handling
  useEffect(() => {
    try {
      const client = createClient();
      setSupabase(client);
    } catch (error) {
      console.error('Failed to create Supabase client:', error);
      setIsLoading(false);
    }
  }, []);

  // Check admin status (super_admin and platform admin) when user changes
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user || !supabase) {
        setIsSuperAdmin(false);
        setIsAdmin(false);
        return;
      }

      try {
        // Check super_admin status
        const { data: isSuperAdminData, error: superError } = await supabase.rpc('is_super_admin', { p_user_id: user.id });
        
        if (superError) {
          console.error('Error calling is_super_admin RPC:', superError);
        } else {
          setIsSuperAdmin(!!isSuperAdminData);
        }
        
        // Check platform admin status (includes super_admin)
        const { data: isAdminData, error: adminError } = await supabase.rpc('is_platform_admin', { p_user_id: user.id });
        
        if (adminError) {
          console.error('Error calling is_platform_admin RPC:', adminError);
          setIsAdmin(false);
        } else {
          setIsAdmin(!!isAdminData);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsSuperAdmin(false);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user, supabase]);

  useEffect(() => {
    if (!supabase) {
      setIsLoading(false);
      return;
    }

    const getInitialSession = async () => {
      // Add a timeout to prevent hanging
      const timeoutId = setTimeout(() => {
        console.warn('Session check timed out, setting loading to false');
        setIsLoading(false);
      }, 10000); // 10 second timeout

      try {
        const {
          data: { session: currentSession },
          error,
        } = await supabase.auth.getSession();
        
        clearTimeout(timeoutId);
        
        // Handle refresh token errors gracefully
        if (error) {
          // Check if it's a refresh token error (case-insensitive check)
          const errorMessage = error.message?.toLowerCase() || '';
          const isRefreshTokenError = 
            errorMessage.includes('refresh token') ||
            errorMessage.includes('refresh_token_not_found') ||
            errorMessage.includes('refresh token not found') ||
            (error.name === 'AuthApiError' && errorMessage.includes('invalid refresh token'));
          
          if (isRefreshTokenError) {
            // Clear invalid session silently - this is expected when refresh token is missing/invalid
            setSession(null);
            setUser(null);
            // Clear any stale auth data
            try {
              await supabase.auth.signOut();
            } catch {
              // Ignore sign out errors during cleanup
            }
            setIsLoading(false);
            return;
          }
          
          // Log other errors but don't break the flow
          console.error('Session error:', error);
        }
        
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
      } catch (error: any) {
        clearTimeout(timeoutId);
        
        // Handle unexpected errors
        const errorMessage = error?.message?.toLowerCase() || '';
        const isRefreshTokenError = 
          errorMessage.includes('refresh token') ||
          errorMessage.includes('refresh_token_not_found') ||
          errorMessage.includes('refresh token not found') ||
          (error?.name === 'AuthApiError' && errorMessage.includes('invalid refresh token'));
        
        if (isRefreshTokenError) {
          // Silently handle refresh token errors - this is expected behavior
          setSession(null);
          setUser(null);
          try {
            await supabase.auth.signOut();
          } catch {
            // Ignore sign out errors during cleanup
          }
        } else {
          // Log other unexpected errors
          console.error('Unexpected session error:', error);
        }
      } finally {
        clearTimeout(timeoutId);
        setIsLoading(false);
      }
    };

    getInitialSession();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (isLoading) setIsLoading(false);
        switch (event) {
          case 'SIGNED_IN':
            if (newSession?.user) {
              await checkAndInstallSupernovaAgent(newSession.user.id, newSession.user.created_at);
            }
            break;
          case 'SIGNED_OUT':
            // Clear local storage when user is signed out (handles all logout scenarios)
            clearUserLocalStorage();
            break;
          case 'TOKEN_REFRESHED':
            break;
          case 'MFA_CHALLENGE_VERIFIED':
            break;
          default:
        }
      },
    );

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, [supabase]); // Removed isLoading from dependencies to prevent infinite loops

  const signOut = async () => {
    if (!supabase) return;
    try {
      await supabase.auth.signOut();
      // Clear local storage after successful sign out
      clearUserLocalStorage();
    } catch (error) {
      console.error('❌ Error signing out:', error);
    }
  };

  const value = {
    supabase,
    session,
    user,
    isLoading,
    isSuperAdmin,
    isAdmin,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
